/**
 * Detects an open modal dialog outside the panel (spec section 7). One MutationObserver on `body`,
 * coalesced per animation frame, active only while the panel is not closed.
 */
import { useEffect } from 'react';

const OWN = '[data-feedback-panel],[data-feedback-pill]';

/** `data-*` attributes of an element; `undefined` only for a non-HTML/SVG/MathML element. */
function dataOf(el: Element): DOMStringMap | undefined {
  return (el as Partial<HTMLElement>).dataset;
}

function isLive(el: Element): boolean {
  if (el.closest(OWN)) return false;
  if (dataOf(el)?.state === 'closed') return false;
  if (el.hasAttribute('hidden')) return false;
  return true;
}

function nativeModalDialogs(): Element[] {
  try {
    return Array.from(document.querySelectorAll('dialog:modal'));
  } catch {
    // `:modal` is unsupported in some engines (and jsdom); clauses 1, 2 and 4 still apply.
    return [];
  }
}

/** True when a modal dialog (not the panel itself) is open. Exported for tests. */
export function isModalOpen(): boolean {
  const explicit = [
    ...Array.from(document.querySelectorAll('[aria-modal="true"]')),
    ...Array.from(document.querySelectorAll('[role="alertdialog"]')),
    ...nativeModalDialogs(),
  ];
  if (explicit.some(isLive)) return true;
  // Presence check: Radix sets `data-scroll-locked` (often to an empty string) while a modal locks scroll.
  if (document.body.dataset.scrollLocked === undefined) return false;
  return Array.from(document.querySelectorAll('[role="dialog"][data-state="open"]')).some(isLive);
}

export function useModalOpen(active: boolean, onChange: (open: boolean) => void): void {
  useEffect(() => {
    if (!active) return undefined;
    let frame = 0;
    const evaluate = (): void => {
      frame = 0;
      onChange(isModalOpen());
    };
    const schedule = (): void => {
      if (frame === 0) frame = requestAnimationFrame(evaluate);
    };
    evaluate();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', 'data-state', 'data-scroll-locked', 'open', 'role', 'hidden'],
    });
    return () => {
      observer.disconnect();
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [active, onChange]);
}
